import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config';

function EyeIcon({ visible }) {
  return visible ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function Login({ onLoginSuccess }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('verified')) {
      setInfoMessage(t('login.emailVerified'));
    } else if (params.get('verify_error') === 'missing_token') {
      setError(t('login.verifyErrorMissing'));
    } else if (params.get('verify_error')) {
      setError(t('login.verifyErrorInvalid'));
    }
    if (params.get('verified') || params.get('verify_error')) {
      window.history.replaceState({}, '', window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/v1/auth/login`, {
        email,
        password,
      });
      onLoginSuccess(response.data.access_token);
    } catch (err) {
      if (err.response && err.response.status === 429) {
        setError(t('login.tooManyAttempts'));
      } else {
        setError(t('login.invalidCredentials'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-panel min-h-screen flex items-center justify-center font-sans px-4">
      <form
        onSubmit={handleSubmit}
        className="bg-surface border border-edge rounded-2xl p-8 sm:p-10 w-full max-w-[360px]"
      >
        <h1 className="text-accent text-xl font-extrabold mb-1">{t('common.appName')}</h1>
        <p className="text-muted text-[13px] mb-7">{t('login.subtitle')}</p>

        {infoMessage && (
          <p className="text-accent text-[13px] mb-4">{infoMessage}</p>
        )}

        <label className="text-[#888] text-xs block mb-1.5">{t('login.email')}</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="w-full bg-surfaceAlt border border-edge rounded-lg px-3 py-2.5 text-white text-sm mb-4 box-border focus:outline-none focus:border-accent"
        />

        <label className="text-[#888] text-xs block mb-1.5">{t('login.password')}</label>
        <div className="relative mb-5">
          <input
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="w-full bg-surfaceAlt border border-edge rounded-lg px-3 py-2.5 pr-10 text-white text-sm box-border focus:outline-none focus:border-accent"
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted cursor-pointer"
            tabIndex={-1}
          >
            <EyeIcon visible={showPassword} />
          </button>
        </div>

        <p className="text-right mb-4">
          <span className="text-accent text-xs cursor-pointer" onClick={() => navigate('/forgot-password')}>
            {t('forgotPassword.linkText')}
          </span>
        </p>

        {error && (
          <p className="text-red-400 text-[13px] mb-4">{error}</p>
        )}

        <button
          type="submit"
          disabled={loading}
          className={`w-full bg-accent border-none rounded-lg py-3 text-panel text-sm font-bold cursor-pointer ${loading ? 'opacity-70' : ''}`}
        >
          {loading ? t('login.submitting') : t('login.submit')}
        </button>
      </form>
    </div>
  );
}

export default Login;